import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  Mail,
  KeyRound,
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertCircle,
  Smartphone,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import { authApi } from "../lib/authApi";

interface PendingUser {
  _id: string;
  email: string;
  username: string;
  firstName?: string;
  lastName?: string;
  role: string;
  avatar?: string;
  isEmailVerified: boolean;
}

const TwoFactorPendingPage = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const pendingUser = (() => {
    try {
      const raw = localStorage.getItem("pendingLogin");
      return raw ? (JSON.parse(raw) as PendingUser) : null;
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    if (!pendingUser) {
      navigate("/login", { replace: true });
    }
  }, [pendingUser, navigate]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (codeInputRefs.current[0]) {
      codeInputRefs.current[0].focus();
    }
  }, []);

  // Auto-verify when all digits are filled
  useEffect(() => {
    const fullCode = code.join("");
    if (fullCode.length === 6 && !loading && !verifying) {
      handleVerify(fullCode);
    }
  }, [code]);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(0, 1);
    setCode(newCode);
    setError("");

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    // Handle backspace to go to previous input
    if (e.key === "Backspace" && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);

    // Focus the next empty input after paste
    const nextEmptyIndex = newCode.findIndex((digit) => !digit);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    codeInputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = async (verificationCode: string) => {
    if (!pendingUser) return;

    setVerifying(true);
    setLoading(true);
    setError("");

    try {
      const response = await authApi.verifyLogin2FA(
        pendingUser.email,
        verificationCode,
      );
      if (!response.success) {
        throw new Error("Invalid verification code");
      }
      localStorage.setItem("accessToken", response.accessToken);
      localStorage.setItem("user", JSON.stringify(pendingUser));
      localStorage.removeItem("pendingLogin");
      showSuccess("Login successful!");

      setTimeout(() => {
        switch (pendingUser.role) {
          case "admin":
            window.location.replace("/dashboard/admin");
            break;
          case "instructor":
            window.location.replace("/dashboard/instructor");
            break;
          case "mentor":
            window.location.replace("/dashboard/mentor");
            break;
          default:
            window.location.replace("/dashboard/student");
        }
      }, 500);
    } catch (err: any) {
      const msg = err?.message || "Invalid verification code";
      setError(msg);
      showError(msg);
      setCode(["", "", "", "", "", ""]);
      codeInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!pendingUser) return;
    setResending(true);
    try {
      const response = await authApi.send2FACode(pendingUser.email);
      if (response.success) {
        showSuccess("Verification code resent!");
        setCode(["", "", "", "", "", ""]);
        codeInputRefs.current[0]?.focus();
        setError("");
      } else {
        throw new Error("Failed to resend code");
      }
    } catch (err: any) {
      showError(err?.message || "Failed to resend verification code");
    } finally {
      setResending(false);
    }
  };

  if (!pendingUser) return null;

  const fullCode = code.join("");
  const isCodeComplete = fullCode.length === 6;

  return (
    <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-orange-500/20 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>

      <div className="relative w-full max-w-sm">
        {/* Main Card */}
        <div className="overflow-hidden bg-white border border-gray-100 rounded-xl shadow-lg">
          {/* Header - Compact */}
          <div className="px-4 py-3 text-center bg-gradient-to-r from-orange-600 to-orange-500">
            <div className="flex items-center justify-center gap-0 font-bold text-center">
              <span className="text-white text-[1.2rem]">Learn</span>
              <span className="text-white text-[1.2rem]">Hub</span>
            </div>
            <p className="mt-1 text-[10px] text-orange-100">
              Two-Factor Authentication
            </p>
          </div>

          {/* Content - Compact */}
          <div className="p-4">
            {/* User Info - Compact */}
            <div className="flex items-center gap-2 p-2 mb-4 bg-orange-50 rounded-lg">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-[11px]">
                  {pendingUser.firstName?.charAt(0) ||
                    pendingUser.email.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-900 truncate">
                  {pendingUser.firstName && pendingUser.lastName
                    ? `${pendingUser.firstName} ${pendingUser.lastName}`
                    : pendingUser.username || pendingUser.email}
                </p>
                <p className="text-[10px] text-gray-500 truncate">
                  {pendingUser.email}
                </p>
              </div>
              <Mail className="w-3 h-3 text-orange-500" />
            </div>

            {/* Instructions - Compact */}
            <div className="mb-4 text-center">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 mb-2 bg-blue-50 rounded-full">
                <KeyRound className="w-2.5 h-2.5 text-blue-500" />
                <span className="text-[9px] font-medium text-blue-600">
                  Verification Required
                </span>
              </div>
              <p className="text-[11px] text-gray-600">
                Enter the 6-digit code sent to your email
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-2 mb-3 border border-red-200 rounded-lg bg-red-50 animate-shake">
                <p className="text-[11px] text-red-600 text-center">{error}</p>
              </div>
            )}

            {/* Verification in Progress */}
            {isCodeComplete && !error && verifying && (
              <div className="p-2 mb-3 border border-blue-200 rounded-lg bg-blue-50 animate-pulse">
                <div className="flex items-center justify-center gap-1">
                  <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />
                  <p className="text-[10px] text-blue-600">Verifying...</p>
                </div>
              </div>
            )}

            {/* Digit Inputs - Compact */}
            <div className="mb-4">
              <label className="block mb-2 text-[11px] font-medium text-gray-700 text-center">
                Verification Code
              </label>
              <div className="flex justify-center gap-2" onPaste={handlePaste}>
                {[...Array(6)].map((_, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      codeInputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={code[index]}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className={`w-8 h-8 text-center text-base font-bold rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500/20
                      ${
                        code[index]
                          ? "border-orange-500 bg-orange-50 text-orange-700"
                          : error
                            ? "border-red-300 bg-red-50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                      }
                      ${isCodeComplete && !error && !verifying ? "border-green-500 bg-green-50" : ""}`}
                  />
                ))}
              </div>
              <p className="mt-2 text-center text-[9px] text-gray-400">
                Enter the 6-digit code sent to your email
              </p>
            </div>

            {/* Action Buttons - Compact */}
            <div className="space-y-2">
              <button
                onClick={() => handleVerify(code.join(""))}
                disabled={!isCodeComplete || loading}
                className={`w-full py-1.5 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-1 text-[11px]
                  ${
                    isCodeComplete && !loading
                      ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white hover:shadow-md"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-3 h-3" />
                    Verify & Continue
                  </>
                )}
              </button>

              <button
                onClick={handleResendCode}
                disabled={resending}
                className="w-full py-1.5 text-[11px] font-medium text-orange-600 hover:text-orange-700 transition-colors disabled:opacity-50"
              >
                {resending ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                    Sending...
                  </>
                ) : (
                  "Didn't receive a code? Resend"
                )}
              </button>

              <button
                onClick={() => {
                  localStorage.removeItem("pendingLogin");
                  navigate("/login", { replace: true });
                }}
                className="w-full py-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" />
                Back to login
              </button>
            </div>

            {/* Security Note - Compact */}
            <div className="flex items-center justify-center gap-1 mt-4 pt-3 border-t border-gray-100">
              <Shield className="w-3 h-3 text-gray-400" />
              <p className="text-[9px] text-gray-400">
                Code expires in 10 minutes
              </p>
            </div>
          </div>
        </div>

        {/* Help Text - Compact */}
        <p className="mt-3 text-center text-[9px] text-gray-400">
          Having trouble?{" "}
          <a href="/contact" className="text-orange-500 hover:underline">
            Contact support
          </a>
        </p>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default TwoFactorPendingPage;